import React, {useMemo, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {
    getCapsuleStats,
    getUserData,
    sealCapsule,
    setSealModalStatus
} from "../../../store/cabinet/action";
import {
    CAPSULE_SECTION_WEIGHT,
    capsulePercentHandler,
    capsuleSectionPointsHandler,
    pluralHandler,
    storageHandler,
    storageSizeHandler
} from "../../../helpers/capsule";

// ---------------------------------------------------------------------------
// Закрытие капсулы необратимо: после него анкета, заметки и файлы становятся
// доступны только на чтение, вернуть статус open нельзя. Поэтому одним
// нажатием не закрываем — просим подтвердить галочкой.
// ---------------------------------------------------------------------------

const CabinetCapsuleSealModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const opened = useSelector(store => store.cabinet.sealModalStatus);
    const capsuleStats = useSelector(store => store.cabinet.capsuleStats);
    const sealStarted = useSelector(store => store.cabinet.sealStarted);
    const sealErr = useSelector(store => store.cabinet.sealErr);

    const [agreed, setAgreed] = useState(false);

    const stats = capsuleStats?.stats;
    const percent = capsulePercentHandler(stats);
    // количество файлов не ограничено — показываем занятое место
    const storage = storageHandler(false, capsuleStats?.storage);

    const parts = useMemo(()=>([
        {
            name: t('Анкета'),
            value: `${stats?.questionnaire?.answered || 0} ${t('из')} ${stats?.questionnaire?.total || 0}`,
            percent: Math.min(stats?.questionnaire?.percent || 0, 100),
            points: capsuleSectionPointsHandler(stats, 'questionnaire'),
            weight: CAPSULE_SECTION_WEIGHT.questionnaire
        },
        {
            name: t('Материалы'),
            value: storage.known && storage.quota
                ? `${storageSizeHandler(storage.used)} ${t('из')} ${storageSizeHandler(storage.quota)}`
                : `${stats?.files?.count || 0} ${pluralHandler(stats?.files?.count || 0, [t('файл'), t('файла'), t('файлов')])}`,
            percent: Math.min(stats?.files?.percent || 0, 100),
            points: capsuleSectionPointsHandler(stats, 'files'),
            weight: CAPSULE_SECTION_WEIGHT.files
        },
        {
            name: t('Заметки'),
            value: `${stats?.notes?.count || 0} ${t('из')} ${stats?.notes?.max || 0}`,
            percent: Math.min(stats?.notes?.percent || 0, 100),
            points: capsuleSectionPointsHandler(stats, 'notes'),
            weight: CAPSULE_SECTION_WEIGHT.notes
        }
    ]),[stats, storage, t])

    const closeModal = () => {
        setAgreed(false);
        dispatch(setSealModalStatus(false));
    };

    const sealHandler = () => {
        if (!agreed || sealStarted){
            return;
        }

        dispatch(sealCapsule(()=>{
            // статус капсулы живёт и в stats, и в user — обновляем оба
            dispatch(getCapsuleStats());
            dispatch(getUserData());
            closeModal();
        }));
    };

    // 422 errors.capsule — капсула уже закрыта или ещё не открыта
    const errorText = useMemo(()=>{
        if (!sealErr){
            return '';
        }
        const errors = sealErr?.errors;
        if (errors?.capsule?.length){
            return errors.capsule[0];
        }
        return sealErr?.message || t('Не удалось закрыть капсулу');
    },[sealErr, t])

    return (
        <Popup
            open={opened}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeModal}>
                    <div/>
                    <div/>
                </div>

                <div className={`${Styles.modalContentWrap}`}>
                    <div className={`${Styles.modalHead}`}>
                        <div className={`${Styles.modalTitle}`}>{t('Закрыть капсулу')}</div>
                        <div className={`${Styles.modalNote}`}>
                            {t('После закрытия содержимое капсулы больше нельзя будет изменить')}
                        </div>
                    </div>

                    <div className={`${Styles.modalBody}`}>
                        <div className={`${Styles.percentWrap}`}>
                            <span className={`${Styles.percentName}`}>{t('Капсула заполнена на')}</span>
                            <span className={`${Styles.percentValue}`}>{percent}%</span>
                        </div>

                        <div className={`${Styles.partsWrap}`}>
                            {parts.map((item, index)=>(
                                <React.Fragment key={'seal-part_' + item.name}>
                                    {index ? <div className={`${Styles.partDivider}`}/> : null}
                                    <div className={`${Styles.partRow}`}>
                                        <span className={`${Styles.partName}`}>{item.name}</span>
                                        <span className={`${Styles.partValue}`}>{item.value}</span>
                                        <span className={`${Styles.partPercent}`}>
                                            {item.points} / {item.weight}%
                                        </span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>

                        <div className={`${Styles.warnRow}`}>
                            <span className={`${Styles.warnIcon}`}>!</span>
                            <span>
                                {t('Это действие необратимо: добавить ответы, заметки и файлы после закрытия будет нельзя')}
                            </span>
                        </div>

                        <div
                            className={`${Styles.agreeRow}`}
                            onClick={()=>{setAgreed(!agreed)}}
                        >
                            <span className={`${Styles.agreeBox} ${agreed ? Styles.agreeBoxOn : ''}`}>
                                {agreed ? '✓' : ''}
                            </span>
                            <span className={`${Styles.agreeText}`}>
                                {t('Я понимаю, что капсулу больше нельзя будет изменить')}
                            </span>
                        </div>

                        {errorText ? <div className={`${Styles.errNote}`}>{errorText}</div> : null}
                    </div>

                    <div className={`${Styles.modalFooter}`}>
                        <div className={`${Styles.modalBtnGhost}`} onClick={closeModal}>
                            {t('Отмена')}
                        </div>
                        <div
                            className={`${Styles.modalBtn} ${(!agreed || sealStarted) ? Styles.modalBtnDsb : ''}`}
                            onClick={sealHandler}
                        >
                            {sealStarted ? t('Закрываем…') : t('Закрыть капсулу навсегда')}
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetCapsuleSealModal;
